/**
 * Types for checkout.com Flow (CheckoutWebComponents).
 *
 * The SDK is loaded directly from checkout.com (never bundled — PCI
 * requirement) and registers `window.CheckoutWebComponents`.
 */

type CSSProperties = Record<string, string | number>

export interface FlowLinkInfo {
  href?: string | null
  title?: string | null
  link?: string | null
}

/**
 * The payment session object the Flow SDK expects, i.e. checkout.com's
 * Create-a-Payment-Session API response shape (snake_case) — not the
 * camelCase PaymentSessionResponse our backoffice gateway returns.
 * Map with `toFlowPaymentSession` in CheckoutFlowService.
 *
 * ⚠️ Key names to be verified against the checkout.com sandbox — see the
 * migration plan, "Field mapping note".
 */
export interface FlowPaymentSession {
  id: string
  payment_session_token: string
  payment_session_secret: string
  _links?: Record<string, FlowLinkInfo>
}

export interface FlowComponent {
  mount(element: string | HTMLElement): FlowComponent
  unmount(): void
  isValid(): boolean
  isAvailable(): Promise<boolean>
}

export interface FlowPaymentResponse {
  id: string
  status?: string
}

export interface FlowComponentError {
  code?: string
  message?: string
}

// Error codes the Flow component surfaces to the shopper itself (validation
// shown inline, declines displayed with next actions) — so the app must not
// also show its own error banner for them. Everything else is unhandled.
export const HANDLED_FLOW_ERROR_CODES = [
  "component_invalid",
  "payment_request_declined",
]

export interface CheckoutWebComponentsAppearance {
  colorAction?: string
  colorBackground?: string
  colorBorder?: string
  colorDisabled?: string
  colorError?: string
  colorFormBackground?: string
  colorFormBorder?: string
  colorInverse?: string
  colorOutline?: string
  colorPrimary?: string
  colorSecondary?: string
  colorSuccess?: string
  borderRadius?: [string, string]
  button?: CSSProperties
  footnote?: CSSProperties
  label?: CSSProperties
  subheading?: CSSProperties
  focusOutlineOffset?: string
  focusOutlineWidth?: string
}

export type CheckoutWebComponentsEnvironment = "sandbox" | "production"

export type FlowComponentType = "flow" | "card"

export interface CheckoutWebComponentsConfig {
  publicKey: string
  environment: CheckoutWebComponentsEnvironment
  paymentSession: FlowPaymentSession
  locale?: string
  appearance?: CheckoutWebComponentsAppearance
  componentOptions?: Record<string, unknown>
  onReady?: (component: FlowComponent) => void
  onChange?: (component: FlowComponent) => void
  onPaymentCompleted?: (
    component: FlowComponent,
    paymentResponse: FlowPaymentResponse,
  ) => void
  onError?: (component: FlowComponent, error: FlowComponentError) => void
}

export interface CheckoutWebComponentsInstance {
  create(componentType: FlowComponentType): FlowComponent
}

export type CheckoutWebComponentsFunction = (
  config: CheckoutWebComponentsConfig,
) => Promise<CheckoutWebComponentsInstance>
